import type { ComponentProps } from 'react';
import React from 'react';
import {
  AdvancedSearchButton,
  Box,
  Button,
  Drawer,
  Flex,
  Grid,
  Input,
  InputCheckBox,
  SingleComboBox,
  RiAddBoxLine,
  RiDownload2Line
} from '@itandi/itandi-bb-ui';
import { FormProvider, useForm } from 'react-hook-form';
import { useRouter } from 'next/router';
import type { DealSearchCondition } from '~/models/Deal';
import { searchDetailBadge } from '~/models/Deal';
import type { CompanyVendor } from '~/models/CompanyVendor';
import { convertToComboBoxOption } from '~/models/CompanyVendor';
import Styles from './DealSearch.module.scss';
import { useDebounce } from '~/hooks/useDebounce';
import { DealSearchDetailTab } from '~/components/specifics/deals/index/search/DealSearchDetailTab';
import { PATH as NEW_PATH } from '~/pages/deals/new';
import { renderSearchButton } from '~/components/common/parts/search/SearchButton';
import { requestCsvGenerate } from '~/utils/api/CsvGenerateResult';
import DealsCsvExportModal from '~/components/common/dealsCsvExports/DealsCsvExportModal';
import useItandiAccount from '~/hooks/useItandiAccount';

type Props = {
  dealSearchCondition: DealSearchCondition;
  handleChangeCondition: (newCondition: DealSearchCondition) => Promise<void>;
  companyVendors: CompanyVendor[];
  isSp: boolean;
};

export const DealSearch: React.FC<Props> = ({
  dealSearchCondition,
  handleChangeCondition,
  companyVendors,
  isSp
}: Props) => {
  const { data: account } = useItandiAccount().currentAccount();
  const methods = useForm<DealSearchCondition>({
    mode: 'onChange',
    defaultValues: dealSearchCondition
  });
  const router = useRouter();
  const [propertyDealSearchValues, setPropertyDealSearchValues] =
    React.useState({
      name: dealSearchCondition.name ?? '',
      roomNumber: dealSearchCondition.roomNumber ?? ''
    });
  const [isOpenSearchDetailTab, setIsOpenSearchDetailTab] =
    React.useState(false);
  const selectedCompanyVendor =
    companyVendors.find(
      ({ id }) => id === dealSearchCondition.companyVendorId
    ) || null;
  const [selectedCompanyVendorOption, setSelectedCompanyVendorOption] =
    React.useState<ComponentProps<typeof SingleComboBox>['value'] | null>(
      selectedCompanyVendor
        ? convertToComboBoxOption(selectedCompanyVendor)
        : null
    );
  const handleOpenSearchDetailTab = React.useCallback((): void => {
    setIsOpenSearchDetailTab(true);
  }, []);
  const handleCloseSearchDetailTab = React.useCallback((): void => {
    setIsOpenSearchDetailTab(false);
  }, []);
  const debounce = useDebounce(200);
  const createOnChange = React.useCallback(
    (field: keyof Pick<DealSearchCondition, 'name' | 'roomNumber'>) =>
      async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        const newValue = e.target.value;
        const newCondition: DealSearchCondition = {
          ...dealSearchCondition,
          [field]: newValue
        };
        setPropertyDealSearchValues((prev) => ({ ...prev, [field]: newValue }));
        methods.setValue(field, newValue);
        debounce(() => {
          handleChangeCondition(newCondition).then();
        });
      },
    [dealSearchCondition, debounce, handleChangeCondition, methods]
  );
  const onChangeName = createOnChange('name');
  const onChangeRoomNumber = createOnChange('roomNumber');
  const onChangeCompanyVendor = React.useCallback(
    async (
      option: ComponentProps<typeof SingleComboBox>['value'] | null
    ): Promise<void> => {
      setSelectedCompanyVendorOption(option);
      const newCondition: DealSearchCondition = {
        ...dealSearchCondition,
        companyVendorId: option?.value ? Number(option.value) : undefined
      };
      methods.setValue(
        'companyVendorId',
        option?.value ? Number(option.value) : undefined
      );
      await handleChangeCondition(newCondition);
    },
    [dealSearchCondition, handleChangeCondition, methods]
  );
  const handleDetailSearch = React.useCallback(async (): Promise<void> => {
    const newCondition = methods.getValues();
    // 簡易検索の入力欄に詳細検索で入力した値を適応する
    setPropertyDealSearchValues({
      name: newCondition.name ?? '',
      roomNumber: newCondition.roomNumber ?? ''
    });
    const newSelectedCompanyVendor =
      companyVendors.find(
        (companyVendor) => companyVendor.id === newCondition.companyVendorId
      ) || null;
    setSelectedCompanyVendorOption(
      newSelectedCompanyVendor
        ? convertToComboBoxOption(newSelectedCompanyVendor)
        : null
    );
    handleCloseSearchDetailTab();
    await handleChangeCondition(newCondition);
  }, [
    methods,
    companyVendors,
    handleCloseSearchDetailTab,
    handleChangeCondition
  ]);
  const [csvExportModalOpen, setCsvExportModalOpen] = React.useState(false);
  const handleOpenCsvExport = React.useCallback(
    () => setCsvExportModalOpen(true),
    []
  );
  const handleCloseCsvExport = React.useCallback(
    () => setCsvExportModalOpen(false),
    []
  );
  return (
    <div className={Styles.SimpleSearchContainer}>
      <FormProvider {...methods}>
        {isSp ? (
          <>
            <Grid gap={8}>
              <Input
                onChange={onChangeName}
                placeholder="物件名"
                type="search"
                value={propertyDealSearchValues.name}
              />
              <Input
                onChange={onChangeRoomNumber}
                placeholder="部屋番号"
                type="search"
                value={propertyDealSearchValues.roomNumber}
              />
            </Grid>
            <Flex justifyContent="flex-end" marginTop={8}>
              <AdvancedSearchButton
                badge={searchDetailBadge(dealSearchCondition)}
                onClick={handleOpenSearchDetailTab}
              />
            </Flex>
          </>
        ) : (
          <>
            <Flex
              alignItems="center"
              justifyContent="space-between"
              marginBottom={8}
            >
              <Grid
                alignItems="center"
                autoFlow="column"
                gap={8}
                justifyContent="start"
              >
                <Box width={240}>
                  <Input
                    onChange={onChangeName}
                    placeholder="物件名"
                    type="search"
                    value={propertyDealSearchValues.name}
                  />
                </Box>
                <Box width={120}>
                  <Input
                    onChange={onChangeRoomNumber}
                    placeholder="部屋番号"
                    type="search"
                    value={propertyDealSearchValues.roomNumber}
                  />
                </Box>
                <Box width={180}>
                  <SingleComboBox
                    defaultValue={selectedCompanyVendorOption || null}
                    onChange={onChangeCompanyVendor}
                    options={companyVendors.map((c: CompanyVendor) =>
                      convertToComboBoxOption(c)
                    )}
                    placeholder="施工会社"
                    value={selectedCompanyVendorOption}
                  />
                </Box>
                <Box>
                  <AdvancedSearchButton
                    badge={searchDetailBadge(dealSearchCondition)}
                    onClick={handleOpenSearchDetailTab}
                  />
                </Box>
                <Box>
                  <InputCheckBox
                    checked={methods.getValues('prioritySort')}
                    label="未読を優先表示する"
                    onChange={(e): void => {
                      const newCondition: DealSearchCondition = {
                        ...dealSearchCondition,
                        prioritySort: e.target.checked
                      };
                      methods.setValue('prioritySort', e.target.checked);
                      handleChangeCondition(newCondition).then();
                    }}
                  />
                </Box>
              </Grid>
              <Grid
                alignItems="center"
                autoFlow="column"
                gap={8}
                justifyContent="end"
              >
                {account && !account.isAdmin && (
                  <>
                    <Button
                      onClick={handleOpenCsvExport}
                      startIcon={<RiDownload2Line />}
                      variant="primary"
                    >
                      CSV出力
                    </Button>
                    <DealsCsvExportModal
                      handleClose={handleCloseCsvExport}
                      isOpen={csvExportModalOpen}
                      requestCsvGenerate={requestCsvGenerate}
                      searchParams={dealSearchCondition}
                    />
                  </>
                )}
                <Button
                  onClick={(): void => {
                    router.push(NEW_PATH);
                  }}
                  startIcon={<RiAddBoxLine />}
                  variant="primary"
                >
                  追加
                </Button>
              </Grid>
            </Flex>
          </>
        )}

        <div className={Styles.SearchDetailContainer}>
          <Drawer
            bottomArea={renderSearchButton(handleDetailSearch)}
            isOpen={isOpenSearchDetailTab}
            onClickBackdrop={handleCloseSearchDetailTab}
            onClickCloseButton={handleCloseSearchDetailTab}
            title="詳細検索"
          >
            <DealSearchDetailTab
              companyVendors={companyVendors}
              dealSearchCondition={dealSearchCondition}
              selectedCompanyVendor={selectedCompanyVendor}
            />
          </Drawer>
        </div>
      </FormProvider>
    </div>
  );
};
